import { useEffect, useState } from "react";
import { Button, Download, EmailPreview, EmptyState, Modal, Tags } from "../../../ui";
import { cancelDashboardRequestAPI, downloadMenuEmailAttachmentAPI } from "../../../../utils/api/ludDashboard";
import { getRequestEmailsAPI, retryRequestEmailAPI } from "../../../../utils/api/requestEmails";
import { getUnifiedRequestAPI } from "../../../../utils/api/unifiedRequests";
import { appName, branchName, formatDateTime, actorText } from "./requestHelpers";
import ActionDialog from "./ActionDialog";
import SendEmailDialog from "./SendEmailDialog";

// Menu "Send to App" e-mail requests (dashboard action "menuAppEmail"), on their Requests row.
//
//   Draft      Preview, Send, Cancel
//   Failed     Details (shows the error), Retry (with confirmation), Cancel
//   Sent       Details only
//   Cancelled  Details (shows the cancellation reason)
//
// Sending always goes through the shared confirmed flow (SendEmailDialog: preview, explicit
// confirmation, hash-checked send with an idempotency key); a retry re-sends the stored
// message after its own confirmation; a cancellation needs a written reason. What is offered
// comes from the API's `actions`; the server checks role, restaurant and state again.
export const MENU_EMAIL_ACTION = "menuAppEmail";
export const isMenuEmail = (item) => item?.identity?.source === "dashboard" && item?.row?.action === MENU_EMAIL_ACTION;

const PURPOSE = { code: "menu.appEmail", label: { ar: "إرسال أصناف القائمة إلى التطبيق", en: "Send menu items to the app" } };
const formatSize = (bytes) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : bytes >= 1024 ? `${Math.round(bytes / 1024)} KB` : `${bytes} B`);
const STATUS_VARIANT = { draft: "neutral", failed: "danger", sent: "success", canceled: "neutral" };

// The details of one request: store / branch / app, the full e-mail, the file, status and dates.
export function MenuEmailDetailsDialog({ item, t, lang, branches, onClose }) {
  const token = localStorage.getItem("auth-token");
  const m = t.menuEmail;
  const [state, setState] = useState({ phase: "loading", request: null });
  const [downloadError, setDownloadError] = useState("");

  useEffect(() => {
    let alive = true;
    getUnifiedRequestAPI(token, item.identity.source, item.identity.id)
      .then((request) => alive && setState({ phase: "ready", request }))
      .catch(() => alive && setState({ phase: "error", request: null }));
    return () => {
      alive = false;
    };
  }, [token, item.identity.source, item.identity.id]);

  const request = state.request;
  const email = request?.details?.email;
  const status = request?.status?.native;

  const download = async (file) => {
    setDownloadError("");
    try {
      const blob = await downloadMenuEmailAttachmentAPI(token, item.identity.id, file.index);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setDownloadError(m.downloadFailed);
    }
  };

  const rows = request
    ? [
        [m.fields.status, <Tags key="s" size="sm" variant={STATUS_VARIANT[status] || "neutral"} appearance="soft">{m.status[status] || request.status.label?.[lang === "ar" ? "ar" : "en"]}</Tags>],
        [m.fields.store, email?.storeName || request.scope?.storeId],
        [m.fields.branch, request.scope?.branchId ? branchName(request.scope.branchId, branches, lang) : m.noBranch],
        [m.fields.app, appName(email?.app, lang) || email?.appLabel?.[lang === "ar" ? "ar" : "en"]],
        [m.fields.action, `${m.actions[email?.itemAction] || email?.itemAction || ""} · ${email?.itemCount ?? 0}`],
        [m.fields.reference, email?.reference || request.reference],
        [m.fields.createdAt, formatDateTime(request.createdAt, lang)],
        [m.fields.createdBy, actorText(request.createdBy)],
        email?.sentAt && [m.fields.sentAt, formatDateTime(email.sentAt, lang)],
        status === "canceled" && email?.cancelReason && [m.fields.reason, email.cancelReason],
        status === "failed" && request.details?.applyError && [m.fields.error, request.details.applyError],
      ].filter(Boolean)
    : [];

  return (
    <Modal title={m.detailsTitle(item.reference)} size="lg" onClose={onClose} closeLabel={m.close} className="lud-ur-modal lud-ur-menu-email" footer={<Button variant="neutral" appearance="soft" onClick={onClose}>{m.close}</Button>}>
      {state.phase === "loading" && <EmptyState variant="loading" compact>{m.loading}</EmptyState>}
      {state.phase === "error" && <EmptyState variant="error" compact title={m.loadFailed} />}
      {state.phase === "ready" && (
        <div className="lud-ur-dialog-body">
          <dl className="lud-ur-menu-email-facts">
            {rows.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd dir="auto" className={label === m.fields.error ? "lud-ur-error" : undefined}>{value || "—"}</dd>
              </div>
            ))}
          </dl>
          {email && (
            <>
              <EmailPreview message={{ to: email.to, cc: email.cc, subject: email.subject, text: email.text, html: email.html, attachments: (email.attachments || []).map((file) => ({ ...file, sha256: String(file.index) })) }} labels={t.email} lang={lang} />
              {(email.attachments || []).length > 0 && (
                <section aria-label={m.fields.attachment}>
                  <h4>{m.fields.attachment}</h4>
                  <ul className="lud-ur-plain">
                    {email.attachments.map((file) => (
                      <li key={file.index} className="lud-ur-menu-email-file">
                        <span dir="auto">{file.filename} <small>({formatSize(file.size)})</small></span>
                        <Button size="sm" variant="primary" appearance="soft" icon={<Download size={14} />} onClick={() => download(file)}>{m.download}</Button>
                      </li>
                    ))}
                  </ul>
                  {downloadError && <p className="lud-ur-error" role="alert">{downloadError}</p>}
                </section>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

// Retry of a failed request: confirmation of what is re-sent; an "unknown outcome" e-mail needs an explicit acknowledgement.
function RetryDialog({ item, t, onClose, onDone }) {
  const token = localStorage.getItem("auth-token");
  const m = t.menuEmail;
  const [state, setState] = useState({ phase: "loading", email: null });

  useEffect(() => {
    let alive = true;
    getRequestEmailsAPI(token, item.identity.source, item.identity.id)
      .then((overview) => {
        const email = (overview.emails || []).find((entry) => ["failed", "uncertain"].includes(entry.status));
        if (alive) setState({ phase: "ready", email: email || null });
      })
      .catch(() => alive && setState({ phase: "ready", email: null }));
    return () => {
      alive = false;
    };
  }, [token, item.identity.source, item.identity.id]);

  if (state.phase === "loading") {
    return (
      <Modal title={m.retryTitle} size="md" onClose={onClose} closeLabel={m.close}>
        <EmptyState variant="loading" compact>{m.loading}</EmptyState>
      </Modal>
    );
  }
  const email = state.email;
  return (
    <ActionDialog
      t={t}
      title={m.retryTitle}
      description={email ? m.retryText : m.retryNoEmail}
      confirmLabel={m.retryConfirm}
      ack={email?.status === "uncertain" ? m.retryUncertain : undefined}
      onClose={onClose}
      onConfirm={async () => {
        if (!email) throw new Error(m.retryNoEmail);
        await retryRequestEmailAPI(token, email.id, { acknowledgeUncertain: email.status === "uncertain" });
        await onDone?.();
      }}
    >
      {email && (
        <div className="lud-ur-preview">
          <dl>
            <dt>{t.email.subject}</dt>
            <dd dir="auto">{email.message.subject}</dd>
            <dt>{t.email.to}</dt>
            <dd dir="ltr">{(email.message.to || []).join(", ")}</dd>
            {email.message.cc?.length > 0 && (
              <>
                <dt>{t.email.cc}</dt>
                <dd dir="ltr">{email.message.cc.join(", ")}</dd>
              </>
            )}
            <dt>{t.email.attachments}</dt>
            <dd dir="auto">{(email.message.attachments || []).map((file) => `${file.filename} (${formatSize(file.size)})`).join(", ") || "—"}</dd>
            {email.lastError && (
              <>
                <dt>{m.fields.error}</dt>
                <dd dir="auto" className="lud-ur-error">{email.lastError}</dd>
              </>
            )}
          </dl>
        </div>
      )}
    </ActionDialog>
  );
}

// "Details" for requests without row actions (sent / cancelled).
export function MenuEmailDetailsButton({ item, t, lang, branches }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="neutral" appearance="soft" onClick={() => setOpen(true)}>{t.menuEmail.details}</Button>
      {open && <MenuEmailDetailsDialog item={item} t={t} lang={lang} branches={branches} onClose={() => setOpen(false)} />}
    </>
  );
}

export default function MenuEmailActions({ item, t, lang, branches, onChanged }) {
  const [dialog, setDialog] = useState(null);
  const m = t.menuEmail;
  const a = item.actions || {};
  const status = item.status?.native;
  const close = () => setDialog(null);
  const done = async () => {
    close();
    await onChanged?.();
  };

  return (
    <>
      <Button size="sm" variant="neutral" appearance="soft" onClick={() => setDialog("details")}>{status === "draft" ? m.preview : m.details}</Button>
      {a.send && <Button size="sm" variant="primary" appearance="filled" onClick={() => setDialog("send")}>{m.send}</Button>}
      {a.retry && <Button size="sm" variant="primary" appearance="filled" onClick={() => setDialog("retry")}>{m.retry}</Button>}
      {a.cancel && <Button size="sm" variant="danger" appearance="soft" onClick={() => setDialog("cancel")}>{m.cancel}</Button>}
      {dialog === "details" && <MenuEmailDetailsDialog item={item} t={t} lang={lang} branches={branches} onClose={close} />}
      {dialog === "send" && <SendEmailDialog request={item} purpose={PURPOSE} t={t} lang={lang} onClose={close} onChanged={onChanged} />}
      {dialog === "retry" && <RetryDialog item={item} t={t} onClose={close} onDone={done} />}
      {dialog === "cancel" && (
        <ActionDialog
          t={t}
          title={m.cancelTitle}
          description={m.cancelText}
          reason={{ label: m.cancelReason, required: true, min: 5 }}
          confirmLabel={m.cancelConfirm}
          danger
          onClose={close}
          onConfirm={async (reason) => {
            await cancelDashboardRequestAPI(localStorage.getItem("auth-token"), item.identity.id, reason);
            await done();
          }}
        />
      )}
    </>
  );
}
